import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTaxById, createTax, updateTax, getGeoZones } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditTax() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("taxes");
  const { t: tCommon } = useTranslation("common");

  // TAX_TYPE_OPTIONS / CALC_METHOD_OPTIONS need t(), so they're built here
  // rather than as module-level constants (a module-level array has no
  // hook access).
  const TAX_TYPE_OPTIONS = [
    { value: "vat", label: t("type_vat", "VAT") },
    { value: "sales_tax", label: t("type_sales_tax", "Sales Tax") },
    { value: "service_charge", label: t("type_service_charge", "Service Charge") },
    { value: "excise", label: t("type_excise", "Excise") },
    { value: "customs", label: t("type_customs", "Customs") },
    { value: "other", label: t("type_other", "Other") },
  ];

  const CALC_METHOD_OPTIONS = [
    { value: "percentage", label: t("calc_method_percentage", "Percentage of price") },
    { value: "fixed_amount", label: t("calc_method_fixed", "Fixed amount per unit") },
  ];

  const [geoZones, setGeoZones] = useState([]);
  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "vat",
    calculation_method: "percentage",
    rate: "",
    geo_zone_id: "",
    is_price_inclusive: false,
    is_compound: false,
    is_active: true,
    sort_order: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getGeoZones(token).then(setGeoZones).catch(() => notify.error(t("notify_load_geo_zones_failed", "Failed to load geo zones")));

    if (id) {
      getTaxById(token, id).then((tax) => {
        setForm({
          name: tax.name || "",
          code: tax.code || "",
          type: tax.type || "vat",
          calculation_method: tax.calculation_method || "percentage",
          rate: tax.rate ?? "",
          geo_zone_id: tax.geo_zone_id ? String(tax.geo_zone_id) : "",
          is_price_inclusive: tax.is_price_inclusive || false,
          is_compound: tax.is_compound || false,
          is_active: tax.is_active !== undefined ? tax.is_active : true,
          sort_order: tax.sort_order ?? "",
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load tax")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving this unset means "applies everywhere" — see docs/tax-design.md.
  const geoZoneOptions = [
    { value: "", label: t("region_all_no_restriction", "All regions (no restriction)") },
    ...geoZones.map((g) => ({ value: String(g.id), label: g.name })),
  ];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Tax name is required");
    if (form.rate === "" || Number(form.rate) < 0) newErrors.rate = t("validation_rate_invalid", "Enter a valid, non-negative rate");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type,
      calculation_method: form.calculation_method,
      rate: Number(form.rate),
      geo_zone_id: form.geo_zone_id ? Number(form.geo_zone_id) : 0,
      is_price_inclusive: form.is_price_inclusive,
      is_compound: form.is_compound,
      is_active: form.is_active,
      sort_order: form.sort_order === "" ? 0 : Number(form.sort_order),
    };

    try {
      if (id) {
        await updateTax(token, id, payload);
        notify.success(t("notify_updated", "Tax updated successfully"));
      } else {
        await createTax(token, payload);
        notify.success(t("notify_created", "Tax created successfully"));
      }
      navigate("/taxes");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/taxes")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Tax") : t("add_title", "Add New Tax")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A single, reusable rate — bundle it into a Tax Group to assign it to products")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Tax Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Taxes} />

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_code_label", "Code (optional)")} value={form.code} onChange={setField("code")} />
            <SelectBox
              label={t("field_type_label", "Type")}
              options={TAX_TYPE_OPTIONS}
              value={form.type}
              onChange={(val) => setForm((prev) => ({ ...prev, type: val }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SelectBox
              label={t("field_calc_method_label", "Calculation Method")}
              options={CALC_METHOD_OPTIONS}
              value={form.calculation_method}
              onChange={(val) => setForm((prev) => ({ ...prev, calculation_method: val }))}
            />
            <FloatingInput
              label={form.calculation_method === "fixed_amount" ? t("field_amount_per_unit_label", "Amount per unit") : t("field_rate_percent_label", "Rate %")}
              type="number"
              value={form.rate}
              onChange={setField("rate")}
              error={errors.rate}
            />
          </div>

          <SelectBox
            label={t("field_target_region_label", "Target Region")}
            options={geoZoneOptions}
            value={form.geo_zone_id}
            onChange={(val) => setForm((prev) => ({ ...prev, geo_zone_id: val }))}
          />

          <ToggleSwitch
            label={t("toggle_price_inclusive", "Price Already Includes This Tax")}
            checked={form.is_price_inclusive}
            onChange={(val) => setForm((prev) => ({ ...prev, is_price_inclusive: val }))}
          />
          <ToggleSwitch
            label={t("toggle_compound", "Compound (calculated on top of prior taxes)")}
            checked={form.is_compound}
            onChange={(val) => setForm((prev) => ({ ...prev, is_compound: val }))}
          />
          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <FloatingInput label={t("field_sort_order_label", "Sort Order (optional)")} type="number" value={form.sort_order} onChange={setField("sort_order")} />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_tax", "Update Tax") : t("btn_create_tax", "Create Tax")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/taxes")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
